/**
 * Сохранить квартиру в базу — вручную по ссылке или со страницы сайта
 * (тогда ссылка и название уже подставлены). Повтор той же квартиры не
 * создаёт дубль: сервер найдёт её по нормализованной ссылке.
 */

import { useEffect, useState } from 'react';
import type { StayPropertyDto } from '@tw/shared-types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useSession } from '../../state/session.tsx';
import { describeError } from '../../lib/errors.ts';
import { FormField, INPUT } from './bits.tsx';
import { isListingUrl } from './meta.ts';

interface Props {
  open: boolean;
  initial?: { url?: string; title?: string; photoUrl?: string | null; city?: string | null; country?: string | null } | null;
  onClose: () => void;
  onSaved: (p: StayPropertyDto) => void;
}

export function SavePropertyDialog({ open, initial, onClose, onSaved }: Props) {
  const { api } = useSession();
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setUrl(initial?.url ?? '');
    setTitle(initial?.title ?? '');
    setCity(initial?.city ?? '');
    setCountry(initial?.country ?? '');
    setPhotoUrl(initial?.photoUrl ?? '');
    setError(null);
  }, [open, initial]);

  const submit = async () => {
    if (!isListingUrl(url.trim())) return setError('Нужна ссылка на страницу квартиры Airbnb или Booking');
    if (!title.trim()) return setError('Укажите название');
    setSaving(true);
    setError(null);
    try {
      const p = await api.saveStayProperty({
        url: url.trim(),
        title: title.trim(),
        city: city.trim() || null,
        country: country.trim() || null,
        photoUrl: photoUrl.trim() || null,
      });
      onSaved(p);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Квартира в базу</DialogTitle>
          <DialogDescription>Если она уже есть в базе, откроется существующая запись.</DialogDescription>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-3">
          <FormField label="Ссылка">
            <input className={INPUT} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.booking.com/hotel/…" />
          </FormField>
          <FormField label="Название">
            <input className={INPUT} value={title} onChange={(e) => setTitle(e.target.value)} />
          </FormField>
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Город">
              <input className={INPUT} value={city} onChange={(e) => setCity(e.target.value)} />
            </FormField>
            <FormField label="Страна">
              <input className={INPUT} value={country} onChange={(e) => setCountry(e.target.value)} />
            </FormField>
          </div>
          <FormField label="Ссылка на фото" hint="Необязательно: правой кнопкой по фото на сайте → «Копировать адрес изображения». Только фото с Airbnb или Booking.">
            <input className={INPUT} value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} />
          </FormField>
          {error && <p role="alert" className="rounded-xl bg-destructive/10 px-3 py-2 text-[13px] text-destructive">{error}</p>}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>Отмена</Button>
          <Button size="sm" disabled={saving} onClick={() => void submit()}>{saving ? 'Сохраняю…' : 'Сохранить'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
